// Auth cookie helpers — the admin-web replacement for web-core's CookieUtils
// (auth portion only). Mirrors web/tools/authCookies.ts.
//
// The access + refresh JWTs are set as httpOnly cookies BY THE RAILS API, so
// client JavaScript can no longer read or write them — the browser sends them
// automatically (axios with withCredentials: true). The only client-readable
// auth artifact is the non-secret session-flag cookie, which carries just
// { account_id, exp } so the UI can do login-detection and proactive refresh
// without ever touching the JWT.
//
// Cookie names MUST match what the Rails API sets (see AuthCookies concern):
//   _kw<env> / _kwrt<env> / _kw_session<env>, where <env> === REACT_APP_ENVIRONMENT.

const ENV = process.env.REACT_APP_ENVIRONMENT;

export const sessionFlagCookieName = (env: string | undefined = ENV): string =>
  `_kw_session${env}`;

export interface SessionFlag {
  account_id: number;
  exp: number; // access-token expiry, epoch seconds
}

// Reads a single cookie value from document.cookie (client-side only).
function readRawCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp('(?:^|; )' + escaped + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

// Client-side: parse the readable session flag. Returns null if absent/invalid.
export function readSessionFlag(env: string | undefined = ENV): SessionFlag | null {
  const raw = readRawCookie(sessionFlagCookieName(env));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.account_id != null) {
      return { account_id: Number(parsed.account_id), exp: Number(parsed.exp) };
    }
  } catch (e) {
    // malformed flag — treat as logged out
  }
  return null;
}

// Client-side login detection (replaces the old token-presence check).
export function isLoggedInFromFlag(env: string | undefined = ENV): boolean {
  return readSessionFlag(env) !== null;
}
